import React, { useEffect, useMemo, useRef, useState } from 'react';
import { RiArrowDownSLine, RiArrowUpSLine } from '@remixicon/react';

import { Button, Modal, SearchableDropdown } from '@/components';
import { Field } from '@/design-system/field';
import { Hint } from '@/design-system/hint';
import { Input, InputAffix, InputRoot } from '@/design-system/input';
import { Label } from '@/design-system/label';
import { PasswordInput } from '@/design-system/password-input';
import { toast } from '@/design-system/toaster';

import {
  ClientRole,
  ProviderUser,
  ProviderUserPayload,
  createProviderUser,
  getClientRoles,
  updateProviderUser,
} from '@/services/api/providers';
import { getOutletsByProvider, Outlet } from '@/services/api/outlets';
import { getListPitstop, Pitstop } from '@/services/api/pitstops';

import {
  CLIENT_ROLE,
  CLIENT_ROLE_PERMISSIONS,
  PASSWORD_RULE_HINT,
  toLocalPhone,
  toStoredPhone,
  validateConfirmPassword,
  validateEmail,
  validatePassword,
  validatePhone,
} from './providerUserForm';

interface ProviderUserFormModalProps {
  isOpen: boolean;
  providerId: number | string;
  /** The user being edited; `null` opens the create form. */
  user: ProviderUser | null;
  onClose: () => void;
  onSaved: () => void;
}

type FieldKey = 'role' | 'name' | 'email' | 'phone' | 'pitstops';

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <p className="border-b border-stroke-soft-200 pb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
    {children}
  </p>
);

/**
 * Create / edit a client-portal user. Ported from next-backoffice-web's
 * `CreateUserModal` + `EditUserModal`: the role comes from core's RBAC list
 * and is sent as `roleId`, outlet as `providerOutletId`, pitstops as
 * `providerPitstopIds`.
 */
export const ProviderUserFormModal: React.FC<ProviderUserFormModalProps> = ({
  isOpen,
  providerId,
  user,
  onClose,
  onSaved,
}) => {
  const isEdit = Boolean(user);

  // Options — loaded once per provider, the first time the form opens.
  const [roles, setRoles] = useState<ClientRole[]>([]);
  const [outlets, setOutlets] = useState<Outlet[]>([]);
  const [pitstops, setPitstops] = useState<Pitstop[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const loadedFor = useRef<string | null>(null);

  // Form fields
  const [roleId, setRoleId] = useState('');
  const [outletId, setOutletId] = useState('');
  const [pitstopIds, setPitstopIds] = useState<(string | number)[]>([]);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPermissions, setShowPermissions] = useState(false);

  const [touched, setTouched] = useState<Partial<Record<FieldKey, boolean>>>(
    {}
  );
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen || loadedFor.current === String(providerId)) return;
    let cancelled = false;

    const load = async () => {
      setOptionsLoading(true);
      const [rolesRes, outletsRes, pitstopsRes] = await Promise.allSettled([
        getClientRoles(),
        getOutletsByProvider(providerId),
        getListPitstop({ providerId, page: '1', size: '999' }),
      ]);
      if (cancelled) return;

      if (rolesRes.status === 'fulfilled') {
        setRoles(rolesRes.value);
      } else {
        toast.error(`Failed to load roles: ${rolesRes.reason?.message ?? ''}`);
      }
      if (outletsRes.status === 'fulfilled') {
        setOutlets(outletsRes.value.data ?? []);
      }
      if (pitstopsRes.status === 'fulfilled') {
        setPitstops(pitstopsRes.value.data ?? []);
      }
      // Only cache a complete load — a failed role fetch retries next open.
      if (rolesRes.status === 'fulfilled') {
        loadedFor.current = String(providerId);
      }
      setOptionsLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, providerId]);

  // Reset / prefill every time the form opens.
  useEffect(() => {
    if (!isOpen) return;
    setRoleId(user?.roles?.[0]?.id ?? '');
    setOutletId(
      String(user?.providerOutlet?.id ?? user?.providerOutletId ?? '')
    );
    setPitstopIds(user?.providerPitstops?.map((p) => p.id) ?? []);
    setName(user?.name ?? '');
    setEmail(user?.email ?? '');
    setPhone(toLocalPhone(user?.phoneNumber));
    setPassword('');
    setConfirmPassword('');
    setShowPermissions(false);
    setTouched({});
    setSubmitted(false);
  }, [isOpen, user]);

  // The detail payload carries the role name but not always its id — resolve
  // it against the loaded options.
  useEffect(() => {
    if (!isOpen || roleId || !user?.roles?.[0]?.name) return;
    const match = roles.find((r) => r.name === user.roles?.[0]?.name);
    if (match) setRoleId(match.id);
  }, [isOpen, roleId, roles, user]);

  const selectedRole = roles.find((r) => r.id === roleId);
  const isSuperAdmin = selectedRole?.name === CLIENT_ROLE.SUPER_ADMIN;
  const isSupervisor = selectedRole?.name === CLIENT_ROLE.SUPERVISOR;
  const permissions = selectedRole
    ? CLIENT_ROLE_PERMISSIONS[selectedRole.name]
    : undefined;

  const roleOptions = useMemo(
    () => roles.map((r) => ({ value: r.id, label: r.label || r.name })),
    [roles]
  );
  const outletOptions = useMemo(
    () => outlets.map((o) => ({ value: String(o.id), label: o.name })),
    [outlets]
  );
  const pitstopOptions = useMemo(() => {
    const options = pitstops.map((p) => ({
      value: p.id as string | number,
      label: p.code ? `${p.name} · ${p.code}` : p.name,
    }));
    // Keep the user's current pitstops selectable even if the list missed one.
    user?.providerPitstops?.forEach((p) => {
      if (!options.some((o) => String(o.value) === String(p.id))) {
        options.push({ value: p.id, label: p.name });
      }
    });
    return options;
  }, [pitstops, user]);

  const errors: Record<FieldKey, string> & {
    password: string;
    confirmPassword: string;
  } = {
    role: roleId ? '' : 'Select a role',
    name: name.trim() ? '' : 'Full name is required',
    email: validateEmail(email),
    phone: validatePhone(phone),
    pitstops:
      isSupervisor && pitstopIds.length === 0
        ? 'A supervisor needs at least one pitstop'
        : '',
    password: isEdit ? '' : validatePassword(password),
    confirmPassword: isEdit
      ? ''
      : validateConfirmPassword(password, confirmPassword),
  };
  const hasErrors = Object.values(errors).some(Boolean);

  const errorFor = (key: FieldKey) =>
    submitted || touched[key] ? errors[key] : '';
  const touch = (key: FieldKey) =>
    setTouched((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
  // Password fields validate live once typed into, like the back-office form.
  const passwordError = submitted || password ? errors.password : '';
  const confirmPasswordError =
    submitted || confirmPassword ? errors.confirmPassword : '';

  const handleRoleChange = (value: string) => {
    setRoleId(value);
    touch('role');
    // Super Admin is scoped to every outlet — it can't carry one.
    if (roles.find((r) => r.id === value)?.name === CLIENT_ROLE.SUPER_ADMIN) {
      setOutletId('');
    }
  };

  const handlePhonePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    // Accept a pasted `0812…` / `+62 812…` by trimming it to the local part.
    e.preventDefault();
    setPhone(toLocalPhone(e.clipboardData.getData('text')).slice(0, 13));
    touch('phone');
  };

  const handleSubmit = async (e?: React.FormEvent | React.MouseEvent) => {
    e?.preventDefault();
    setSubmitted(true);
    if (hasErrors || isSubmitting) return;

    const payload: ProviderUserPayload = {
      name: name.trim(),
      email: email.trim(),
      phoneNumber: toStoredPhone(phone),
      roleId,
      providerOutletId: !isSuperAdmin && outletId ? outletId : null,
      providerPitstopIds: pitstopIds.map(Number),
    };

    setIsSubmitting(true);
    try {
      if (user) {
        await updateProviderUser(providerId, user.id, payload);
        toast.success('User updated successfully');
      } else {
        await createProviderUser(providerId, { ...payload, password });
        toast.success('User created successfully');
      }
      onSaved();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save user');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      icon={isEdit ? 'ri-user-settings-line' : 'ri-user-add-line'}
      iconTone="primary"
      title={isEdit ? 'Edit Portal User' : 'Create Portal User'}
      subtitle={
        isEdit
          ? user?.email
          : 'Invite a new client portal user and assign their role'
      }
      width="md"
      closeOnBackdropClick={!isSubmitting}
      closeOnEscape={!isSubmitting}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="white" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={() => handleSubmit()}
            disabled={isSubmitting || optionsLoading}
            data-testid="provider-user-submit"
          >
            {isSubmitting
              ? 'Saving…'
              : isEdit
                ? 'Save Changes'
                : 'Create User'}
          </Button>
        </div>
      }
    >
      <form
        onSubmit={handleSubmit}
        noValidate
        className="space-y-5"
        data-testid="provider-user-form"
      >
        <section className="space-y-3">
          <SectionLabel>Role</SectionLabel>
          <Field data-testid="provider-user-role">
            <Label required>Role</Label>
            <SearchableDropdown
              options={roleOptions}
              value={roleId}
              onChange={handleRoleChange}
              placeholder={optionsLoading ? 'Loading roles…' : 'Select role'}
              loading={optionsLoading}
              allowClear={false}
              error={Boolean(errorFor('role'))}
              emptyMessage="No roles available"
            />
            {errorFor('role') && <Hint tone="error">{errorFor('role')}</Hint>}
          </Field>

          {permissions &&
            (permissions.can.length > 0 || permissions.cannot.length > 0) && (
              <div>
                <button
                  type="button"
                  onClick={() => setShowPermissions((v) => !v)}
                  aria-expanded={showPermissions}
                  className="inline-flex items-center gap-1 text-xs font-semibold tracking-tight text-accent hover:underline"
                >
                  {showPermissions ? 'Hide' : 'View'} role permissions
                  {showPermissions ? (
                    <RiArrowUpSLine className="size-4" aria-hidden />
                  ) : (
                    <RiArrowDownSLine className="size-4" aria-hidden />
                  )}
                </button>
                {showPermissions && (
                  <div className="mt-2 space-y-3 rounded-gsm border border-stroke-soft-200 p-3">
                    {permissions.can.length > 0 && (
                      <div>
                        <p className="label-eyebrow mb-1.5">
                          What {selectedRole?.label} can do
                        </p>
                        <ul className="space-y-1">
                          {permissions.can.map((desc) => (
                            <li
                              key={desc}
                              className="flex items-center gap-2 text-xs text-text-strong-950"
                            >
                              <i className="ri-checkbox-circle-line text-success-base" />
                              {desc}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {permissions.cannot.length > 0 && (
                      <div>
                        <p className="label-eyebrow mb-1.5">
                          What {selectedRole?.label} can't do
                        </p>
                        <ul className="space-y-1">
                          {permissions.cannot.map((desc) => (
                            <li
                              key={desc}
                              className="flex items-center gap-2 text-xs text-text-strong-950"
                            >
                              <i className="ri-close-circle-line text-error-base" />
                              {desc}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
        </section>

        <section className="space-y-3">
          <SectionLabel>Details</SectionLabel>

          <Field>
            <Label htmlFor="provider-user-name" required>
              Full Name
            </Label>
            <InputRoot size="md" invalid={Boolean(errorFor('name'))}>
              <Input
                id="provider-user-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => touch('name')}
                placeholder="e.g. John Doe"
                autoComplete="off"
              />
            </InputRoot>
            {errorFor('name') && <Hint tone="error">{errorFor('name')}</Hint>}
          </Field>

          <Field data-testid="provider-user-outlet">
            <Label optional>Assigned Outlet</Label>
            <SearchableDropdown
              options={outletOptions}
              value={outletId}
              selectedLabelFallback={user?.providerOutlet?.name}
              onChange={(value) => setOutletId(value)}
              placeholder="All outlets"
              disabled={isSuperAdmin}
              loading={optionsLoading}
              emptyMessage="This client has no outlets"
            />
            {isSuperAdmin && (
              <Hint>Super Admin always has access to every outlet.</Hint>
            )}
          </Field>

          <Field data-testid="provider-user-pitstops">
            <Label required={isSupervisor} optional={!isSupervisor}>
              Assigned Pitstops
            </Label>
            <SearchableDropdown
              multiple
              options={pitstopOptions}
              value={pitstopIds}
              onChange={(values) => {
                setPitstopIds(values);
                touch('pitstops');
              }}
              placeholder="Select pitstops"
              loading={optionsLoading}
              error={Boolean(errorFor('pitstops'))}
              emptyMessage="This client has no pitstops"
            />
            {errorFor('pitstops') && (
              <Hint tone="error">{errorFor('pitstops')}</Hint>
            )}
          </Field>

          <Field>
            <Label htmlFor="provider-user-email" required>
              Email
            </Label>
            <InputRoot size="md" invalid={Boolean(errorFor('email'))}>
              <Input
                id="provider-user-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => touch('email')}
                placeholder="e.g. john@partner.com"
                autoComplete="off"
              />
            </InputRoot>
            {errorFor('email') && (
              <Hint tone="error">{errorFor('email')}</Hint>
            )}
          </Field>

          <Field>
            <Label htmlFor="provider-user-phone" optional>
              Phone Number
            </Label>
            <InputRoot size="md" invalid={Boolean(errorFor('phone'))}>
              <InputAffix className="font-mono text-text-sub-600">+62</InputAffix>
              <Input
                id="provider-user-phone"
                type="tel"
                inputMode="numeric"
                maxLength={13}
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                onPaste={handlePhonePaste}
                onBlur={() => touch('phone')}
                placeholder="8123xxxxxxx"
                className="font-mono tabular-nums"
              />
            </InputRoot>
            {errorFor('phone') && (
              <Hint tone="error">{errorFor('phone')}</Hint>
            )}
          </Field>

          {!isEdit && (
            <>
              <Field>
                <Label htmlFor="provider-user-password" required>
                  Password
                </Label>
                <PasswordInput
                  id="provider-user-password"
                  size="md"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  invalid={Boolean(passwordError)}
                  autoComplete="new-password"
                  showLeadingIcon={false}
                />
                {passwordError ? (
                  <Hint tone="error">{passwordError}</Hint>
                ) : (
                  <Hint>{PASSWORD_RULE_HINT}</Hint>
                )}
              </Field>

              <Field>
                <Label htmlFor="provider-user-confirm-password" required>
                  Confirm Password
                </Label>
                <PasswordInput
                  id="provider-user-confirm-password"
                  size="md"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  invalid={Boolean(confirmPasswordError)}
                  autoComplete="new-password"
                  showLeadingIcon={false}
                />
                {confirmPasswordError && (
                  <Hint tone="error">{confirmPasswordError}</Hint>
                )}
              </Field>
            </>
          )}
        </section>

        {/* Lets Enter submit from any text field. */}
        <button type="submit" hidden aria-hidden tabIndex={-1} />
      </form>
    </Modal>
  );
};

export default ProviderUserFormModal;
